// src/layouts/Navbar.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import logoWhite from "../../assets/logo/logoiconwhite.png";

interface NavLink {
  label: string;
  path?: string;
  subMenu?: { label: string; path: string; description?: string }[];
  external?: boolean; // Add flag for external links
}

// Lock Icon (admin sign-in)
const LockIcon: React.FC = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
    />
  </svg>
);

// Chevron Down Icon
const ChevronDown: React.FC<{ isOpen?: boolean }> = ({ isOpen = false }) => (
  <svg
    className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

export const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const navLinks: NavLink[] = [
    { label: "About", path: "/about" },
    {
      label: "Courses",
      subMenu: [
        {
          label: "DIB",
          path: "/dbc",
          description: "Digital Income Blueprint",
        },
        {
          label: "Faith that Works",
          path: "/faith-that-works",
          description: "Practical Faith Principles",
        },
      ],
    },
    {
      label: "Business Funnel",
      subMenu: [
        {
          label: "Incubator Tech",
          path: "/incubator-tech",
          description: "Tech Solutions for Startups",
        },
      ],
    },
    {
      label: "Events",
      subMenu: [
        {
          label: "Ignite 360",
          path: "/events/ignite-360",
          description: "Transform Your Vision",
        },
        {
          label: "Monetize Your Knowledge",
          path: "/events/monetize-knowledge",
          description: "Turn Expertise into Income",
        },
      ],
    },
    { label: "Wealth Method", path: "/wealth-method", external: true },
  ];

  const toggleDropdown = (label: string) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  const closeAllMenus = () => {
    setIsOpen(false);
    setOpenDropdown(null);
  };

  return (
    <nav className="bg-brandNavy text-white sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 hover:opacity-80 transition"
            onClick={closeAllMenus}
          >
            <img src={logoWhite} alt="Logo" className="h-5 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) =>
              link.subMenu ? (
                // Dropdown Menu
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(link.label)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button className="flex items-center gap-1 text-white/90 hover:text-white font-medium transition py-2">
                    {link.label}
                    <ChevronDown isOpen={openDropdown === link.label} />
                  </button>

                  {/* Dropdown Content */}
                  {openDropdown === link.label && (
                    <div className="absolute top-full left-0 pt-2">
                      <div className="w-64 bg-white text-brandNavy rounded-lg shadow-xl py-2 z-50">
                        {link.subMenu.map((subLink) => (
                          <Link
                            key={subLink.path}
                            to={subLink.path}
                            onClick={closeAllMenus}
                            className="block px-4 py-3 hover:bg-gray-50 transition"
                          >
                            <div className="font-semibold">{subLink.label}</div>
                            {subLink.description && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                {subLink.description}
                              </div>
                            )}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : link.external ? (
                // External Link (opens in new tab)
                <a
                  key={link.label}
                  href={link.path!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/90 hover:text-white font-medium transition"
                >
                  {link.label}
                </a>
              ) : (
                // Regular Link
                <Link
                  key={link.label}
                  to={link.path!}
                  className="text-white/90 hover:text-white font-medium transition"
                  onClick={closeAllMenus}
                >
                  {link.label}
                </Link>
              )
            )}

            {/* Admin Sign In */}
            <Link
              to="/sign-in"
              className="flex items-center gap-1.5 border border-white/25 text-white/90 px-4 py-2 rounded-lg font-medium hover:bg-white/10 hover:text-white transition"
              onClick={closeAllMenus}
            >
              <LockIcon />
              Admin
            </Link>

            {/* CTA Button */}
            <Link
              to="/dbc/buy"
              className="bg-brandRed text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-brandRedHover transition"
              onClick={closeAllMenus}
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden flex flex-col gap-1.5 p-2"
            aria-label="Toggle menu"
          >
            <span
              className={`w-6 h-0.5 bg-white transition-all ${
                isOpen ? "rotate-45 translate-y-2" : ""
              }`}
            ></span>
            <span
              className={`w-6 h-0.5 bg-white transition-all ${
                isOpen ? "opacity-0" : ""
              }`}
            ></span>
            <span
              className={`w-6 h-0.5 bg-white transition-all ${
                isOpen ? "-rotate-45 -translate-y-2" : ""
              }`}
            ></span>
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-white/10">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) =>
                link.subMenu ? (
                  // Mobile Dropdown
                  <div key={link.label}>
                    <button
                      onClick={() => toggleDropdown(link.label)}
                      className="w-full flex items-center justify-between text-white/90 hover:text-white font-medium transition py-2"
                    >
                      {link.label}
                      <ChevronDown isOpen={openDropdown === link.label} />
                    </button>

                    {/* Mobile Submenu */}
                    {openDropdown === link.label && (
                      <div className="pl-4 mt-2 space-y-2">
                        {link.subMenu.map((subLink) => (
                          <Link
                            key={subLink.path}
                            to={subLink.path}
                            onClick={closeAllMenus}
                            className="block text-white/80 hover:text-white text-sm py-2"
                          >
                            <div className="font-medium">{subLink.label}</div>
                            {subLink.description && (
                              <div className="text-xs text-white/60 mt-0.5">
                                {subLink.description}
                              </div>
                            )}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : link.external ? (
                  // External Mobile Link (opens in new tab)
                  <a
                    key={link.label}
                    href={link.path!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/90 hover:text-white font-medium transition py-2"
                  >
                    {link.label}
                  </a>
                ) : (
                  // Regular Mobile Link
                  <Link
                    key={link.label}
                    to={link.path!}
                    onClick={closeAllMenus}
                    className="text-white/90 hover:text-white font-medium transition py-2"
                  >
                    {link.label}
                  </Link>
                )
              )}

              {/* Mobile Admin Sign In */}
              <Link
                to="/sign-in"
                onClick={closeAllMenus}
                className="flex items-center justify-center gap-1.5 border border-white/25 text-white/90 px-6 py-3 rounded-lg font-medium hover:bg-white/10 hover:text-white transition text-center mt-2"
              >
                <LockIcon />
                Admin Sign In
              </Link>

              {/* Mobile CTA */}
              <Link
                to="/dbc/buy"
                onClick={closeAllMenus}
                className="bg-brandRed text-white px-6 py-3 rounded-lg font-semibold hover:bg-brandRedHover transition text-center mt-2"
              >
                Get Started
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
